import type { ReactNode } from 'react';
import type { ResourceCost } from '@kaeliux/shared';
import { cn } from '../utils/cn';
import ResourceCostList from './ResourceCostList';
import { ProgressBar } from './ui/ProgressBar';

interface BuildableThumbProps {
    /** The `<img>`/placeholder visual — must fill a `w-full h-full` box. */
    image: ReactNode;
    countBadge: number;
    countTitle?: string;
    cost: ResourceCost;
    costLabel?: string;
    /** 0–100. Omit to render no progress bar (e.g. ships have no levels). */
    progressPct?: number;
    progressTitle?: string;
    highlighted?: boolean;
    /** Width + aspect-ratio classes for the image box. Defaults to the
     * portrait shape building sprites were authored at; ships are rendered
     * landscape and pass a wider, shorter box instead. */
    imageBoxClassName?: string;
}

/**
 * A larger building/ship thumbnail with a level/docked-count badge on its top
 * right corner, an optional level-progress bar underneath, and its resource
 * cost listed alongside — all wrapped in one tile-like bordered block.
 */
export default function BuildableThumb({
    image,
    countBadge,
    countTitle,
    cost,
    costLabel,
    progressPct,
    progressTitle,
    highlighted,
    imageBoxClassName = 'w-24 sm:w-28 aspect-[3/4]',
}: BuildableThumbProps) {
    return (
        <div
            className={cn(
                'flex gap-3 shrink-0 rounded-tile border p-2 bg-space-950/40 shadow-e2 transition-colors',
                highlighted
                    ? 'border-2 border-status-warning animate-highlight-blink'
                    : 'border-space-700 hover:border-[color:var(--zone-accent)]',
            )}
        >
            <div className="flex flex-col gap-1.5 shrink-0">
                <div className={`relative ${imageBoxClassName}`}>
                    {image}
                    <span
                        className="absolute -top-1.5 -right-1.5 min-w-[1.375rem] h-[1.375rem] px-1 flex items-center justify-center rounded-full bg-space-800 border border-space-600 text-content-primary text-label leading-none shadow-e1"
                        title={countTitle}
                    >
                        {countBadge}
                    </span>
                </div>
                {progressPct !== undefined && (
                    <ProgressBar
                        value={Math.min(100, Math.max(0, progressPct)) / 100}
                        accent="zone"
                        title={progressTitle}
                    />
                )}
            </div>
            <ResourceCostList cost={cost} label={costLabel} />
        </div>
    );
}
